import useSWR from 'swr';
import { secretsService, isApiError } from '@dopomogai/api-client';
import type { SecretResponse, SecretCreateRequest } from '@dopomogai/types';
import { AgentKitError } from '../types';

export interface SecretUsageToolRef {
  tool_id: string;
  tool_name?: string | null;
  env_var_names?: string[] | null;
}

export interface SecretUsageMcpRef {
  server_id: string;
  server_name?: string | null;
  header_keys?: string[] | null;
}

export interface SecretUsageResponse {
  secret_id: string;
  name: string;
  usage_count?: number | null;
  last_used_at?: string | null;
  tools?: SecretUsageToolRef[];
  mcp_servers?: SecretUsageMcpRef[];
}

export const useSecrets = () => {
  const { data, error, isLoading, mutate } = useSWR<SecretResponse[], AgentKitError>(
    'secrets/list',
    async () => {
      const res = await secretsService.list();
      if (isApiError(res)) throw new AgentKitError(res.message, res);
      return res;
    }
  );

  const createSecret = async (payload: SecretCreateRequest) => {
    const res = await secretsService.create(payload);
    if (isApiError(res)) throw new AgentKitError(res.message, res);
    // Important: plaintext should not be kept in FE state after success
    await mutate();
    return res;
  };

  const deleteSecret = async (secretId: string) => {
    const res = await secretsService.delete(secretId as any);
    if (isApiError(res)) throw new AgentKitError(res.message, res);
    await mutate();
    return res;
  };

  const rotateSecret = async (secretId: string, newPlaintextValue: string) => {
    const res = await (secretsService as any).rotate(secretId, { plaintext_secret_value: newPlaintextValue });
    if (isApiError(res)) throw new AgentKitError(res.message, res);
    await mutate();
    return res;
  };

  const getUsage = async (secretId: string): Promise<SecretUsageResponse> => {
    const res = await (secretsService as any).getUsage(secretId);
    if (isApiError(res)) throw new AgentKitError(res.message, res);
    return res as SecretUsageResponse;
  };

  return {
    secrets: data ?? [],
    isLoading,
    error,
    createSecret,
    deleteSecret,
    rotateSecret,
    getUsage,
    mutate,
  };
};